import type { DraftListItem, DraftListResponse, HandlingClass } from "@/components/review/types"

/**
 * Welche Zeilen die Review-Liste zeigt — ohne React, damit testbar.
 *
 * 🔴 „Markiert" gilt ueber ALLE Reiter (#164, Sanitec 07.10.2026: „markierte
 * Mails sind nicht in Review"). Die Liste kam bis dahin nur fuer den offenen
 * Reiter vom Server, der Filter „Markiert" filterte also nur darin. Am
 * Meldetag standen alle vier sichtbaren markierten Mails unter „Aufgabe" —
 * auf dem Standard-Reiter „Antworten" zeigte die Fahne 0.
 * Schon das Flag-Feature (04.05.2026) war als reiteruebergreifend gedacht.
 */
export function reviewAuswahl(
  alle: DraftListItem[],
  { klasse, nurMarkierte }: { klasse: HandlingClass; nurMarkierte: boolean },
): DraftListItem[] {
  if (nurMarkierte) return alle.filter((i) => i.flagged)
  return alle.filter((i) => (i.handling_class || "antworten") === klasse)
}

/** Herkunfts-Filter (#26): Portal-Zeilen vs. echte Nachrichten von aussen. */
export function nachHerkunft(
  zeilen: DraftListItem[], herkunft: "alle" | "extern" | "portal",
): DraftListItem[] {
  if (herkunft === "portal") return zeilen.filter((i) => i.is_proactive)
  if (herkunft === "extern") return zeilen.filter((i) => !i.is_proactive)
  return zeilen
}

/**
 * Wohin „Naechster ↓" (J) und K springen.
 *
 * 🔴 Steht die offene Mail nicht (mehr) in der Liste, war der Knopf tot
 * (#165, Sanitec 07.10.2026). Das passiert, sobald ein Neuplanen sie in
 * einen anderen Reiter schiebt — bei der Meldung wurde aus „Antworten" nach
 * drei Hinweisen an den Planer „Aufgabe". Dann geht es mit der ersten Zeile
 * der Liste weiter statt gar nicht.
 */
export function nachbarId(
  zeilen: DraftListItem[], offeneId: number | null, richtung: 1 | -1,
): number | null {
  if (zeilen.length === 0) return null
  const idx = offeneId ? zeilen.findIndex((i) => i.email_id === offeneId) : -1
  if (idx < 0) return zeilen[0].email_id
  return zeilen[idx + richtung]?.email_id ?? null
}

/** Wie viele „Aeltere laden" auf einmal nachholt, und die Obergrenze der API. */
export const NACHLADEN_SCHRITT = 100
export const LIMIT_MAX = 2000

export type NachladeHinweis = {
  geladen: number
  /** Echte Zahl im Reiter — null, wenn der Server sie nicht kennt. */
  gesamt: number | null
  /** Davon aelter als 30 Tage — null, wenn unbekannt. */
  aelter: number | null
  /** Es fehlen Eintraege; „Aeltere laden" hilft. */
  fehlt: boolean
}

/**
 * Hinweis „100 von 1214 — aeltere laden" fuer den offenen Reiter.
 *
 * 🔴 Nie stillschweigend weniger (08.10.2026): die Liste holte die neuesten
 * 100 Zeilen fuer ALLE Reiter zusammen und zaehlte nur die — bei Sanitec
 * standen 8 von 64 offenen Antworten drin. Jetzt kommt die echte Zahl vom
 * Server. Kennt er sie nicht, wird keine Zahl gezeigt statt einer falschen.
 * „Markiert" braucht keinen Hinweis: Fahnen kommen immer alle.
 */
export function nachladeHinweis(
  daten: DraftListResponse | undefined,
  { klasse, nurMarkierte }: { klasse: HandlingClass; nurMarkierte: boolean },
): NachladeHinweis | null {
  if (!daten || nurMarkierte) return null
  const geladen = daten.geladen?.[klasse] ?? 0
  const gesamt = daten.counts ? daten.counts[klasse] : null
  const aelter = daten.aelter_30_tage ? daten.aelter_30_tage[klasse] : null
  const fehlt = gesamt !== null ? geladen < gesamt : !!daten.gekuerzt
  if (!fehlt && !aelter) return null
  return { geladen, gesamt, aelter, fehlt }
}

/** Reiter-Zahl: die echte Zahl, sonst nichts (nie eine erfundene 0). */
export function reiterZahl(daten: DraftListResponse | undefined, klasse: HandlingClass): number | null {
  return daten?.counts ? daten.counts[klasse] : null
}
